<script setup>
import { computed } from 'vue'
import { Panel, VueFlow, useVueFlow } from '@vue-flow/core'
import '@vue-flow/core/dist/style.css'
import { useI18n } from 'vue-i18n'

import RunVideoMatchNode from './RunVideoMatchNode.vue'
import { buildRunVideoMatchGraph } from './run-video-match-graph.js'

const props = defineProps({
  turns: { type: Array, required: true },
  selectedNodeId: { type: String, required: true },
})
const emit = defineEmits(['select'])

const { t } = useI18n()
const nodeTypes = { videoMatch: RunVideoMatchNode }
const graph = computed(() => buildRunVideoMatchGraph(props.turns, props.selectedNodeId))
const { fitView, setViewport, zoomIn, zoomOut } = useVueFlow()
const DEFAULT_VIEWPORT = { x: 48, y: 72, zoom: 1 }

function showAll() {
  return fitView({ padding: 0.08, maxZoom: 1 })
}
</script>

<template>
  <VueFlow
    class="run-video-match-graph"
    :nodes="graph.nodes"
    :edges="graph.edges"
    :node-types="nodeTypes"
    :default-viewport="DEFAULT_VIEWPORT"
    :min-zoom="0.2"
    :max-zoom="2"
    :nodes-connectable="false"
    @nodes-initialized="showAll"
    @node-click="emit('select', $event.node)"
  >
    <Panel position="top-right" class="run-video-match-graph__zoom">
      <button type="button" :aria-label="t('visualGameAdmin.llm.runVideoMatch.zoomOut')" @click="zoomOut()">−</button>
      <button type="button" @click="setViewport(DEFAULT_VIEWPORT)">100%</button>
      <button type="button" :aria-label="t('visualGameAdmin.llm.runVideoMatch.zoomIn')" @click="zoomIn()">+</button>
      <button type="button" @click="showAll">
        {{ t('visualGameAdmin.llm.runVideoMatch.fitView') }}
      </button>
    </Panel>
  </VueFlow>
</template>

<style lang="scss">
.run-video-match-graph {
  height: 100%;
  background:
    radial-gradient(circle at 1px 1px, color-mix(in srgb, var(--drama-color-text) 8%, transparent) 1px, transparent 0)
    0 0 / 32px 32px,
    var(--drama-color-bg);

  .vue-flow__handle {
    opacity: 0;
  }

  .run-video-match-edge .vue-flow__edge-path {
    stroke-width: 1.75;
  }

  .run-video-match-edge.is-success .vue-flow__edge-path {
    stroke: var(--drama-color-success);
  }

  .run-video-match-edge.is-warning .vue-flow__edge-path {
    stroke: var(--drama-color-warning);
  }

  .run-video-match-edge.is-muted .vue-flow__edge-path {
    stroke: var(--drama-color-border-strong);
    stroke-dasharray: 5 4;
  }

  &__zoom {
    display: flex;
    gap: var(--drama-space-xs);

    button {
      min-width: 40px;
      min-height: var(--drama-control-height-sm);
      padding: 0 var(--drama-space-sm);
      border: 1px solid var(--drama-color-border);
      border-radius: var(--drama-radius-sm);
      background: var(--drama-color-surface);
      color: var(--drama-color-text);
      font-size: var(--drama-font-size-xs);
      cursor: pointer;

      &:hover {
        border-color: var(--drama-color-primary);
        color: var(--drama-color-primary);
      }

      &:focus-visible {
        outline: 2px solid var(--drama-color-primary);
        outline-offset: 2px;
      }
    }
  }
}
</style>
